// import {hel_CallCanvasComponent} from './heliodor_lib';

import {presetBeforeBoot} from './logics/assign';
import {
  ETextParamType_CanvasFileName,
  ETextParamType_CurrentUserName,
  ETextParamType_Logined
} from './logics/const';
import {getCanvasFileName, getCurrentUserName, getXRSession, setXRSession} from './logics/dataOps';
import {setSceneJsonPath} from './logics/dataOps';
import {getLogDOMElement, getStatusDOMElement, getWebGLRenderingContext} from './logics/domOps';
import {hel_canvas_ResetToggleDefault, hel_canvas_ToggleChange} from './logics/gui_caller';
import {
  hel_CallCanvasComponent,
  hel_cancelAnimationFrame,
  hel_requestAnimationFrame,
  hel_setCanvasFileName
} from './logics/lib';
import {getMainCanvasDOMElement} from './logics/mainCanvasDOM';
import {
  hel_setText,
  requestMemoryAllocate,
  requestReleaseMemoryFor,
  requestSetDataToHeap,
  requestSetVrPointer,
  requestSetVrPosePointer,
  requestToBindFrameBufferOf,
  requestToDrawFrame,
  requestToReleaseBoundFrameBuffer,
  requestToSetVrCameraPointer,
  requestVRControllerValue,
  switchToNoneVR,
  switchToVR
} from './logics/moduleCaller';
import {hel_skyway_join, hel_skyway_main} from './logics/skyway';

import type {hel_DispatchEvent} from './logics/moduleCaller';

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  /** HTMLのインラインスクリプトで定義されるグローバル変数。 */
  var xrFileName: string | undefined;
  var hel_skyway_start: (() => Promise<void>) | undefined;
  /* eslint-enable */
}

// 即時実行
// 以下のコードはHTML側にxrStartButtonMultiおよびxrStartButtonSIngle
// という名前のボタンがある時にのみ有効で、そのボタンを押されたら
// WebXRのVR処理のチェックおよび実行が開始される。
// ------------------------------------------------------------------
const xrButtonNameMulti = 'xrStartButtonMulti';
const xrButtonNameSingle = 'xrStartButtonSingle';
const xrButtonMulti = document.getElementById(xrButtonNameMulti)!;
const xrButtonSingle = document.getElementById(xrButtonNameSingle)!;
if (xrButtonSingle !== undefined && xrButtonSingle !== null) {
  xrButtonSingle.onclick = async () => {
    await checkAndInitVR(globalThis.xrFileName);
  };
}
if (xrButtonMulti !== undefined && xrButtonMulti !== null) {
  xrButtonMulti.onclick = async () => {
    hel_skyway_join();
    hel_skyway_main();
    if (!globalThis.hel_skyway_start) {
      throw new Error('hel_skyway_start is not defined');
    }
    await globalThis.hel_skyway_start();
    await checkAndInitVR(globalThis.xrFileName, 1);
  };
}

// --------------------------------------------------------------------

const lastPos = [0.0, 0.0, 0.0]; // 直前(1フレーム前の)のプレイヤーの座標(x,y,z)
let isVRActive = false;

Object.assign(globalThis, {
  hel_checkAndInitVR: async () => {
    await checkAndInitVR(globalThis.xrFileName);
    setIsVRActive(true);
  },

  hel_exitVR: () => {
    const xRSession = getXRSession();
    if (xRSession !== null) {
      xRSession.end();
      setIsVRActive(false);
    }
  },

  hel_setResolutionWebXR: async (rate: number) => {
    const xRSession = getXRSession();
    if (xRSession !== null) {
      const WebglCtx4XR = getWebGLRenderingContext();
      if (!WebglCtx4XR) {
        throw new Error('getWebGLRenderingContext() returns undefined');
      }
      await WebglCtx4XR.makeXRCompatible(); // ここで待たないとXRモードにできない
      const webglLayer = new XRWebGLLayer(xRSession, WebglCtx4XR, {
        framebufferScaleFactor: rate > 1.0 ? 0.75 : 1.0
      });
      await xRSession.updateRenderState({
        baseLayer: webglLayer,
        depthFar: 10000,
        depthNear: 0.1
      });
    }
  },

  /**
   * heliodor_post.jsから呼出し。global関数化
   * @remarks
   * ※この関数がない場合も動作はするが、その場合はXR機能が有効でない時もVRボタンが表示されてしまう。
   */
  XRButtonCheck: () => {
    // navigator.xrはブラウザが提供するxrオブジェクトであり
    // これがnullなら、現在使用中のブラウザがwebxrに対応していない
    // もしくは、現在webxrのデバイスが接続されていないかのどちらかであるため
    // その場合はwebxrのためのボタンを無効化している
    if (xrButtonSingle !== undefined && xrButtonSingle !== null) {
      xrButtonSingle.style.visibility =
        navigator.xr !== undefined && navigator.xr !== null ? 'visible' : 'hidden';
    }
    if (xrButtonMulti !== undefined && xrButtonMulti !== null) {
      xrButtonMulti.style.visibility =
        navigator.xr !== undefined && navigator.xr !== null ? 'visible' : 'hidden';
    }
  }
});

const changeWebXRIcons = (flag: boolean) => {
  if (flag) {
    hel_canvas_ToggleChange('webxr_all');
  } else {
    hel_canvas_ResetToggleDefault('webxr_all');
  }
};

export const setIsVRActive = (flag: boolean): void => {
  isVRActive = flag;
};

export const getIsVRActive = (): boolean => isVRActive;

export const checkVR = () => {
  const XR = window.navigator.xr;
  if (XR) {
    XR.isSessionSupported('immersive-vr')
      .then((supported: boolean) => {
        if (supported) {
          changeWebXRIcons(true);
        } else {
          console.debug('vr is not supported');
          changeWebXRIcons(false);
        }
      })
      .catch((err) => {
        console.debug('support check ' + err.message);
        changeWebXRIcons(false);
      });
  } else {
    console.debug('Is Not Support WebXR On This Browser.');
    changeWebXRIcons(false);
  }
};

/** WebXRのVR機能が使えるかどうかをチェックし、使えるのならアプリのVR版を起動する。
  もし、VR機能使用できない場合はそもそもアプリの起動を行わない。
  @param sceneFile シーンファイル名
  @param IsMulti マルチプレイヤーかどうか(マルチならtrue)
  @remarks
    Promise関数である
*/
export const checkAndInitVR = async (sceneFile?: string, IsMulti = 0) => {
  const XR = window.navigator.xr;
  // ブラウザなどの環境がXRに対応していない場合はこれがfalseになり
  // 以降の処理を行わない
  if (XR) {
    const xrSceneFileName = sceneFile === undefined ? 'Scene/webxrtest.json' : sceneFile;
    XR.isSessionSupported('immersive-vr')
      .then((supported: boolean) => {
        // ここで返されるsupportedによりVR機器が接続されているかどうかがわかる
        if (supported) {
          XR.requestSession('immersive-vr', {requiredFeatures: ['local-floor']})
            .then(async (xRSession) => {
              console.debug('call StartVR');
              // VRありのHeliodor起動
              setXRSession(xRSession);
              // eslint-disable-next-line new-cap
              await StartVR(0, 0, 0, IsMulti, xrSceneFileName, xRSession!);
            })
            .catch((err) => {
              console.debug('immersive-vr check ' + err.message);
            });
        } else {
          // なにも行わずに抜ける
          console.debug('vr is not supported');
        }
      })
      .catch((err) => {
        console.debug('support check ' + err.message);
      });
  } else {
    // そもそもこのブラウザではWebXRが有効にならない
    console.debug('Is Not Support WebXR On This Browser.');
  }
};

/**
 * VRのボタンの値を返す
  @remarks
    gamepad.buttonは場合によってはvalueが取れない事(押してもvalueは0.0)があるため
    pressedフラグを見て0.0or1.0で代用する
  @param button 値を知りたいボタンオブジェクト
  @return 調べたボタンの値
*/
const getVRButtonPressValue = (button: GamepadButton): number => {
  if (button.value === 0.0) {
    return button.pressed ? 1.0 : 0.0;
  } else {
    return button.value;
  }
};

/**
  ２つのボタンの値かpressedの有効な方を返す
  ボタン①に有効な値が入っているならボタン①優先
  両方ともに値もpressedも無効なら0.0を返す
  @param buttonF ボタン①
  @param buttonS ボタン②
*/
const getVROneOfTwoButtonPressedValue = (
  buttonF: GamepadButton | undefined,
  buttonS: GamepadButton | undefined
): number => {
  if (buttonF) {
    const btnF = getVRButtonPressValue(buttonF);
    if (btnF === 0.0) {
      if (buttonS) {
        return getVRButtonPressValue(buttonS);
      }
    } else {
      return btnF;
    }
  } else {
    if (buttonS) {
      return getVRButtonPressValue(buttonS);
    }
  }
  return 0.0;
};

/**
  ２つのオブジェクトの値の有効な方を返す(数値)
  値①に有効なものが入っているなら値①優先
  両方ともに有効な値が入っていないなら0.0を返す
  @param first 値①
  @param second 値②
*/
const getVROneOfTwoValue = (first: number, second: number | undefined): number => {
  if (first === 0.0) {
    // axis[2]有効かつaxis[0]無効の場合は[0]に0.0が入っていることがあるため
    if (second !== undefined) {
      return second;
    }
  } else {
    return first;
  }
  return 0.0;
};

/**
  あるベクトルにクオータニオンによる回転を適用した結果のベクトルを返す
  @param out 回転結果ベクトル
  @param vec 回転したい元ベクトル
  @param q 回転のためのクオータニオン
  @return 回転結果ベクトル
*/
const applyQuaternion = (
  out: [x: number, y: number, z: number],
  vec: [x: number, y: number, z: number],
  q: [x: number, y: number, z: number, w: number]
): number[] => {
  // benchmarks: https://jsperf.com/quaternion-transform-vec3-implementations-fixed
  const qx = q[0];
  const qy = q[1];
  const qz = q[2];
  const qw = q[3];
  const x = vec[0];
  const y = vec[1];
  const z = vec[2];
  let uvx = qy * z - qz * y;
  let uvy = qz * x - qx * z;
  let uvz = qx * y - qy * x;
  let uuvx = qy * uvz - qz * uvy;
  let uuvy = qz * uvx - qx * uvz;
  let uuvz = qx * uvy - qy * uvx;
  const w2 = qw * 2;
  uvx *= w2;
  uvy *= w2;
  uvz *= w2;
  uuvx *= 2;
  uuvy *= 2;
  uuvz *= 2;
  out[0] = x + uvx + uuvx;
  out[1] = y + uvy + uuvy;
  out[2] = z + uvz + uuvz;
  return out;
};
/**
  float配列をC++側に渡せる形にする(バイナリfloatにまとめる)
  @param ptr データを書き込むための先頭アドレス
  @param farray float配列
  @return 書き込み終わった直後のアドレス
*/
const nativizeFloatArray = (ptr: number, farray: Float32Array): number => {
  for (let i = 0; i < farray.length; ++i) {
    HeliModule.setValue(ptr + i * 4, farray[i], 'float');
  }
  return ptr + farray.length * 4;
};

/** 配列全てに１つの値を代入
  C言語で言う所のmemsetのようなことを行う
  @param iarray 代入される側の配列オブジェクト
  @param val 代入される値
*/
const valueToArray = (iarray: Float32Array, val: number) => {
  for (let i = 0; i < iarray.length; ++i) {
    iarray[i] = val;
  }
};

/**
 * 行列情報をC++側に渡せる形にする
 * @param ptr 行列の内容を書き込むためのアドレス
 * @param mat 行列(float配列16個)
 * @return 書き込んだ後ろのアドレス
 */
const NativizeMatrix = (ptr: number, mat: Float32Array) => {
  for (let i = 0; i < 16; ++i) {
    HeliModule.setValue(ptr + i * 4, mat[i], 'float');
  }
  return ptr + 16 * 4;
};

/**
 * 座標をログ出力
 * @param pos 出力したい座標
 * @param name 出力時の名前文字列
 * @remarks
 * デバッグ用
 * */
export const PrintPosition = (pos: any, name: string) => {
  let str = name + ' : ';
  str += 'x=' + pos.x + ' , ';
  str += 'y=' + pos.y + ' , ';
  str += 'z=' + pos.z;
  console.debug(str);
};

/**
 * クオータニオンをログ出力
 * @remarks
 * デバッグ用
 * @param q クオータニオン
 * @param name 出力するとき用のクオータニオンの名前文字列
 * @constructor
 */
export const PrintQuaternion = (q: any, name: string) => {
  let str = name + ' : ';
  str += 'x=' + q.x + ' , ';
  str += 'y=' + q.y + ' , ';
  str += 'z=' + q.z;
  console.debug(str);
};

/**
 * 行列をログ出力
 * @remarks
 * デバッグ用
 * @param mat 行列(中身はfloat*16配列)
 * @param name 出力用の名前文字列
 * @constructor
 */
export const PrintMatrix = (mat: number[], name: string) => {
  let str = name + ' : ';
  for (let i = 0; i < 16; ++i) {
    str += mat[i];
    if (i % 4 === 3) {
      str += '\n';
    } else {
      str += ',';
    }
  }
  console.debug(str);
};

// vrのセッションをスタート
const StartVR = async (
  Type: number,
  CharaType: number,
  Param1: number,
  MultiPlay: number,
  StrParam0: string,
  session: XRSession
) => {
  if (xrButtonMulti === null) {
    console.debug('[ StartVR ] no XrButton Assigned');
    // return;
  } else {
    xrButtonMulti.style.visibility = 'hidden';
  }

  const status = getStatusDOMElement();
  if (status !== undefined) {
    status.style.visibility = 'visible';
  }
  const log = getLogDOMElement();
  if (log !== undefined) {
    log.style.visibility = 'visible';
  }
  session.requestReferenceSpace('local-floor').then(async (RefSpace: XRReferenceSpace) => {
    hel_setCanvasFileName('Canvas/CanvasList.json');
    await hel_start_vr(Type, CharaType, Param1, MultiPlay === 1, StrParam0, session, RefSpace);
  });
};

/**
 * コントローラの状態を出力します
 * @remarks
 * デバッグ用に使用する。現在のセッションのVRコントローラの状態を調べ
 * コンソールに出力する。
 * 注意点：毎フレーム出力するとブラウザがフリーズするのでタイミングに注意
 * @param session WebXRのセッション
 * @constructor
 */
export const OutputVRControllerState = (session: XRSession) => {
  for (const inputSource of session.inputSources) {
    const handedness = inputSource.handedness;
    const gamepad = inputSource.gamepad;
    let str = 'handedness=';
    if (gamepad) {
      str += handedness;
      str += ' , gamepad buttons=';
      let strVal = '';
      let strTouch = '';
      let strPress = '';
      for (const button of gamepad.buttons) {
        strVal += button.value + ',';
        strTouch += button.touched + ',';
        strPress += button.pressed + ',';
      }
      console.debug(str);
      console.debug('val=' + strVal);
      console.debug('touch' + strTouch);
      console.debug('press' + strPress);
      let strAxes = 'axes=';
      for (const axis of gamepad.axes) {
        strAxes += axis + ',';
      }
      console.debug(strAxes);
    } else {
      console.debug('Gamepad is null');
    }
  }
};

// 終了処理
const onXRSessionEnd = async (session: XRSessionEvent) => {
  if (session) {
    console.debug('XRSession Is End object=' + session);
    if (session.session !== null) {
      // session.session.end()//この時点でendしてるので必要なしというエラーメッセージが出るが呼んでおいたほうがいい
      requestToReleaseBoundFrameBuffer();
    }
    switchToNoneVR();
    hel_requestAnimationFrame();
  }
};

// VR系の初期化処理スタート
const hel_start_vr = async (
  Type: number,
  CharaType: number,
  Param1: number,
  MultiPlay: boolean,
  StrParam0: string,
  session: XRSession,
  XRRefSpace: XRReferenceSpace
) => {
  // //XRセッション終了時イベント
  session.addEventListener('end', onXRSessionEnd);
  const mainCanvas = getMainCanvasDOMElement();
  if (mainCanvas === undefined || mainCanvas.width <= 0 || mainCanvas.height <= 0) {
    return;
  }

  const CanTouch = window.ontouchstart !== undefined;

  setSceneJsonPath(globalThis.g_DataFolderName + '/' + StrParam0);
  // Set Parameter
  presetBeforeBoot();
  // デバッグのため、ログイン状態にしておく
  // hel_setText(ETextParamType_Logined, (globalThis.g_Logined ? "1" : "0"));
  hel_setText(ETextParamType_Logined, '1');
  hel_setText(ETextParamType_CanvasFileName, getCanvasFileName());
  hel_setText(ETextParamType_CurrentUserName, getCurrentUserName());

  // requestBootStrapGameEngine(Type, CharaType, Param1, StrParam0, CanTouch, MultiPlay, true);
  // 最後はVRモードかどうか。なので、ここをfalseにするとVRにならない

  //
  mainCanvas.focus();
  const WebglCtx4XR = getWebGLRenderingContext();
  if (!WebglCtx4XR) {
    throw new Error('getWebGLRenderingContext() returns undefined');
  }
  await WebglCtx4XR.makeXRCompatible(); // ここで待たないとXRモードにできない
  // webxr-polyfill.module.jsによってimport * from 'webxr-polyfill'をされてるためグローバルクラスとして利用
  const webglLayer = new XRWebGLLayer(session, WebglCtx4XR);
  await session.updateRenderState({
    baseLayer: webglLayer,
    depthFar: 10000,
    depthNear: 0.1
  });
  const drawFrameVRClosure = (time: number, frame: XRFrame) => {
    drawFrameVR(time, frame, XRRefSpace);
  };

  hel_cancelAnimationFrame();

  if (!session.isSystemKeyboardSupported) {
    console.debug('keyboard is not supported\n');
  } else {
    const textField = <HTMLInputElement>document.createElement('input');
    textField.type = 'text';
    textField.value = 'write in text';
    textField.id = 'vr-text-input';
    textField.addEventListener('change', onChangeVRText);
    document.body.appendChild(textField);
  }

  switchToVR(0, 0);
  session.requestAnimationFrame(drawFrameVRClosure);
};

// VRコントローラの値を取得し、Heliodor側に通知
const updateVRController = (frame: XRFrame, refSpace: XRReferenceSpace) => {
  const session = frame.session;
  // OutputVRControllerState(XRSession);
  const NUM_OF_CONTROLLERS = 2; // 左手、右手(一応対応すべきが左右手のみとはかぎらないためこうしている)
  const CONTROLLER_INFO_NUM = 3 + 4; // 座標3向き4が2つ分でコントローラの場所、向きを渡す
  const GAMEPAD_INFO_NUM = 2 + 6; // レバー軸2方向＋ボタン6個分

  const controllerParams = new Float32Array(CONTROLLER_INFO_NUM * NUM_OF_CONTROLLERS); // コントローラの場所、向き情報を格納
  const gamepadParams = new Float32Array(GAMEPAD_INFO_NUM * NUM_OF_CONTROLLERS); // ゲームパッド情報を格納
  const activates = new Uint8Array(NUM_OF_CONTROLLERS);
  for (let idx = 0; idx < activates.length; ++idx) {
    activates[idx] = 0;
  }
  valueToArray(gamepadParams, 0.0); // gameparams全てに0代入
  // ひとつひとつのinputSourceはXRInputSource
  // https://developer.mozilla.org/en-US/docs/Web/API/XRInputSource
  let controllernum = 0;
  for (const inputSource of session.inputSources) {
    // targetRaySpaceの情報を取得することで、VRコントローラの座標・向きを得る
    const targetRayPose = frame.getPose(inputSource.targetRaySpace, refSpace);
    let controllerIdx = 0;
    // オブジェクトがない場合は無視(あっても手元にない場合は認識しない)
    if (!targetRayPose) {
      console.debug('no pose');
      continue;
    } else {
      if (inputSource.handedness === 'none') {
        console.debug('not vrcontroller');
        continue; // 右でも左でもないものは却下
      }
      controllerIdx = inputSource.handedness === 'left' ? 0 : 1; // 左手なら0 , 右手なら1
    }

    ++controllernum;
    // ここまで来た時点でアクティブ
    activates[controllerIdx] = 1;
    // targetRaySpaceの場合tracked-pointerを使用することになります
    // https://developer.mozilla.org/ja/docs/Web/API/XRInputSource/targetRayMode
    if (inputSource.targetRayMode === 'tracked-pointer') {
      // 現在のポーズのtransfromに現在位置と向きの情報が入っている
      // https://developer.mozilla.org/en-US/docs/Web/API/XRRigidTransform
      // position, orientation, matrixを使用可能
      // 今回はpositionとorientationを渡す
      const idx = controllerIdx * CONTROLLER_INFO_NUM;
      let pos = targetRayPose.transform.position;
      const rot = targetRayPose.transform.orientation;
      if (inputSource.gripSpace) {
        const gripPose = frame.getPose(inputSource.gripSpace, refSpace);
        if (gripPose) {
          pos = gripPose.transform.position;
        }
      }
      controllerParams[idx] = pos.x;
      controllerParams[1 + idx] = pos.y;
      controllerParams[2 + idx] = -pos.z; // Z軸反転

      controllerParams[3 + idx] = -rot.x; // X軸回転反転
      controllerParams[4 + idx] = -rot.y; // Y軸回転反転
      controllerParams[5 + idx] = rot.z; // Z軸回転反転

      controllerParams[6 + idx] = rot.w;

      // gamepad情報を書き込み。詳細は
      // https://hikky.atlassian.net/wiki/spaces/HEL/pages/399474733/WebXR
      // と
      // https://www.w3.org/TR/webxr-gamepads-module/#xr-standard-heading
      // で確認してください
      const gamepad = inputSource.gamepad;
      if (gamepad === null || gamepad === undefined) continue;
      const gpidx = controllerIdx * GAMEPAD_INFO_NUM;

      if (gamepad.axes[0] !== undefined && gamepad.axes[1] !== undefined) {
        gamepadParams[gpidx] = getVROneOfTwoValue(gamepad.axes[0], gamepad.axes[2]); // X軸
        gamepadParams[1 + gpidx] = getVROneOfTwoValue(gamepad.axes[1], gamepad.axes[3]); // Y軸
      }
      if (!gamepad.buttons[0] || !gamepad.buttons[1]) {
        throw new Error(
          `Expected frame.session.inputSources[i].gamepad.buttons.length to be at least 2, but got ${gamepad.buttons.length}.`
        );
      }
      gamepadParams[2 + gpidx] = gamepad.buttons[0].value; // トリガーボタン
      gamepadParams[3 + gpidx] = gamepad.buttons[1].value; // グリップボタン
      // 押し込み。
      const axisPressValue = getVROneOfTwoButtonPressedValue(
        gamepad.buttons[2],
        gamepad.buttons[3]
      );
      gamepadParams[4 + gpidx] = axisPressValue;
      gamepadParams[5 + gpidx] = axisPressValue;

      if (!gamepad.buttons[4]) {
        gamepadParams[6 + gpidx] = 0.0;
        gamepadParams[7 + gpidx] = 0.0;
      } else {
        if (!gamepad.buttons[5]) {
          throw new Error(
            `Expected frame.session.inputSources[i].gamepad.buttons to include index 5 whenever index 4 exists, but length was ${gamepad.buttons.length}.`
          );
        }
        gamepadParams[6 + gpidx] = gamepad.buttons[4].value; // X or A
        gamepadParams[7 + gpidx] = gamepad.buttons[5].value; // Y or B
      }
    } else {
      console.debug('not tracked-pointer');
    }
  }

  const controllerdata = requestMemoryAllocate(controllerParams.length * 4); // コントローラデータ
  const gamepaddata = requestMemoryAllocate(gamepadParams.length * 4); // ゲームパッドデータ
  const activedata = requestMemoryAllocate(activates.length); // アクティブかどうかのデータ
  nativizeFloatArray(controllerdata, controllerParams);
  nativizeFloatArray(gamepaddata, gamepadParams);

  requestSetDataToHeap(activates, activedata);
  const isAvailable = requestVRControllerValue(
    controllernum,
    activedata,
    controllerdata,
    gamepaddata
  );
  requestReleaseMemoryFor(controllerdata);
  requestReleaseMemoryFor(gamepaddata);
  requestReleaseMemoryFor(activedata);
};

// VR用のdrawFrame関数
const drawFrameVR = (time: number, frame: XRFrame, XRRefSpace: XRReferenceSpace) => {
  const session = frame.session;

  const glLayer = session.renderState.baseLayer;
  const viewerPose = frame.getViewerPose(XRRefSpace);
  if (!viewerPose) {
    console.debug('xrsession=' + session);
    console.debug('viewerpose=' + viewerPose);
    console.debug('xrrefspace=' + XRRefSpace);
    console.debug('getViewerPos is Failed');
    console.debug('frame=' + frame);
    // ビューワポーズが確立するまでにはある程度の時間がかかることがあり、その場合はアニメーションフレームを
    // 回しつつ、待つ。
    const drawFrameVRClosure = (t: number, f: XRFrame) => {
      drawFrameVR(t, f, XRRefSpace);
    };
    session.requestAnimationFrame(drawFrameVRClosure);
    return;
  }
  // PrintPosition(viewerPose.transform.position,"headpos");
  // PrintQuaternion(viewerPose.transform.orientation,"headrot");

  const NUM_OF_VRCAMERAS = 2;
  const CAMERA_PARAMS = 7;
  // VRパラメータ用メモリ確保
  const SIZE_OF_VR_VIEWPARAM = (16 + 16 + 4) * 4;
  const data = requestMemoryAllocate(SIZE_OF_VR_VIEWPARAM * NUM_OF_VRCAMERAS);

  const cameraParams = new Float32Array(CAMERA_PARAMS * NUM_OF_VRCAMERAS); // カメラ情報を格納

  if (!glLayer) {
    throw new Error('XRFrame.session.renderState.baseLayer is undefined');
  }

  // C側関数に送るパラメータを作成(左目・右目)
  for (const xrView of viewerPose.views) {
    const viewport = glLayer.getViewport(xrView);
    if (!viewport) {
      throw new Error('XRWebGLLayer.getViewport() returns undefined');
    }
    const eyeIdx = xrView.eye === 'left' ? 0 : 1;

    cameraParams[eyeIdx * CAMERA_PARAMS] = xrView.transform.position.x;
    cameraParams[eyeIdx * CAMERA_PARAMS + 1] = xrView.transform.position.y;
    cameraParams[eyeIdx * CAMERA_PARAMS + 2] = xrView.transform.position.z;
    cameraParams[eyeIdx * CAMERA_PARAMS + 3] = xrView.transform.orientation.x;
    cameraParams[eyeIdx * CAMERA_PARAMS + 4] = xrView.transform.orientation.y;
    cameraParams[eyeIdx * CAMERA_PARAMS + 5] = xrView.transform.orientation.z;
    cameraParams[eyeIdx * CAMERA_PARAMS + 6] = xrView.transform.orientation.w;

    let ptr = data + SIZE_OF_VR_VIEWPARAM * eyeIdx;
    HeliModule.setValue(ptr, viewport.x, 'float');
    ptr += 4;
    HeliModule.setValue(ptr, viewport.y, 'float');
    ptr += 4;
    HeliModule.setValue(ptr, viewport.width, 'float');
    ptr += 4;
    HeliModule.setValue(ptr, viewport.height, 'float');
    ptr += 4;
    const viewMat = xrView.transform.inverse.matrix;
    // let viewMat=xrView.transform.inverse.matrix;

    // カメラのZだけ反転
    if (viewMat[8] === undefined || viewMat[9] === undefined || viewMat[10] === undefined)
      throw new Error();
    viewMat[8] *= -1;
    viewMat[9] *= -1;
    viewMat[10] *= -1;

    // カメラ行列セット
    // eslint-disable-next-line new-cap
    ptr = NativizeMatrix(ptr, viewMat);
    // プロジェクション行列セット
    const projMat = xrView.projectionMatrix;
    // eslint-disable-next-line new-cap
    ptr = NativizeMatrix(ptr, projMat);
  }

  const vrcameradata = requestMemoryAllocate(4 * CAMERA_PARAMS * NUM_OF_VRCAMERAS); // カメラデータ
  nativizeFloatArray(vrcameradata, cameraParams);
  requestToSetVrCameraPointer(vrcameradata);
  requestReleaseMemoryFor(vrcameradata);

  // Added from ver 2.9.0
  const posedata = requestMemoryAllocate((3 + 4) * 4); // 座標(3)＋回転(4)がそれぞれ4バイト分のメモリを確保する
  const poseParams = new Float32Array(3 + 4); //
  poseParams[0] = viewerPose.transform.position.x;
  poseParams[1] = viewerPose.transform.position.y;
  poseParams[2] = viewerPose.transform.position.z;
  poseParams[3] = viewerPose.transform.orientation.x;
  poseParams[4] = viewerPose.transform.orientation.y;
  poseParams[5] = viewerPose.transform.orientation.z;
  poseParams[6] = viewerPose.transform.orientation.w;
  nativizeFloatArray(posedata, poseParams); // Floatの配列をメモリの塊化する(C++とやりとりするため)
  requestSetVrPosePointer(posedata); // 上で作った姿勢情報をC++へ送る
  requestReleaseMemoryFor(posedata); // 確保したメモリを解放

  requestSetVrPointer(data); // ビュー情報をC++へ送る
  updateVRController(frame, XRRefSpace); // VRコントローラの情報をC++側へ送る
  // ここでVR側のコンテキストであるglLayerと、Emscriptenの書き込み先であるctx.FRAMEBUFFERを
  // バインドしておかないと、VR機器に正常に表示されないので注意。
  // https://developer.mozilla.org/ja/docs/Web/API/WebXR_Device_API/Rendering
  // より。
  // 「今後のすべての描画コマンドのターゲットとしてそのフレームバッファーをバインドします」を気に留めておくこと
  requestToBindFrameBufferOf(glLayer); // glBufferのframeBufferとModule.ctx.frameBufferをバインドする

  // C++のDrawFrame関数を呼び出す
  requestToDrawFrame();
  // ビュー情報メモリを解放する
  requestReleaseMemoryFor(data);
  // 座標の補正
  // XRRefSpace=adjustCameraPosition(XRRefSpace);

  const drawFrameVRClosure = (t: number, f: XRFrame) => {
    drawFrameVR(t, f, XRRefSpace);
  };
  session.requestAnimationFrame(drawFrameVRClosure);
};

let m_layerName: string; // VRキーボード用レイヤー名
let m_guiName: string; // VRキーボード用GUI要素名
let m_componentName: string; // VRキーボード用コンポーネント名
let m_methodName: string; // VRキーボード情報戻し用イベント関数名

/**
 * VRの仮想キーボードを表示する
 * @remarks
 * 本関数はグローバル関数用の処理であり、それ以外でのTypeScript層内での呼び出しは禁止。代わりに {@link hel_DispatchEvent} を利用する。
 */
export const hel_show_vr_keyboard = (
  layer: string,
  component: string,
  guiname: string,
  method: string
) => {
  const session = getXRSession();
  if (session !== null) {
    // あらかじめ作っておいたダミーの入力フィールドを取得し、フォーカスをあてる
    // これにより仮想キーボードが表示される(今の所Questのみ)
    const elem = <HTMLInputElement>document.getElementById('vr-text-input');
    if (elem !== null) {
      m_layerName = layer;
      m_componentName = component;
      m_guiName = guiname;
      m_methodName = method;
      elem.focus();
    } else {
      console.debug('vr-text-input is null' + layer + guiname + component + method + '\n');
      hel_CallCanvasComponent(layer, component, method, layer, guiname, 'vrTextIsNull');
    }
  } else {
    console.debug('vrsession is null' + layer + guiname + '\n');
  }
};

/**
 * VRキーボードの入力が完了したイベント
 * @remarks
 * 本関数を、本ファイル外から呼び出せるように転用することは禁止。代わりに {@link hel_DispatchEvent} を利用する。
 */
const onChangeVRText = (ev: Event) => {
  const element: HTMLInputElement = <HTMLInputElement>ev.currentTarget;
  if (element === null) {
    console.debug('EventElement is null');
  }

  const inputEvent: InputEvent = <InputEvent>ev;
  if (inputEvent === null) {
    console.debug('InputEvent is null');
  } else {
    hel_CallCanvasComponent(
      m_layerName,
      m_componentName,
      m_methodName,
      m_layerName,
      m_guiName,
      element.value.toString()
    );
  }
};
